/**
 * chart-source-service.ts
 * =======================
 * Thin event bus for chart data lifecycle events.
 *
 * The large Candle[] is gone — actual column data lives in chart-data-service.ts.
 * This module only carries lightweight metadata so chart-runtime.ts and chartStore
 * know when to rebuild without holding a full candle array in the snapshot.
 */

import { candleAt } from "@/lib/lytick/columnar";
import { getBaseColumns, hasDataset } from "@/lib/lytick/chart-data-service";
import { useChartStore } from "@/stores/chartStore";

export interface ChartSourceSnapshot {
  symbol: string | null;
  /** True when chart-data-service has a loaded dataset. */
  hasData: boolean;
  granularitySeconds: number;
  oldestEpoch: number | null;
  latestEpoch: number | null;
  hasReachedDepthCeiling: boolean;
  isLoadingHistory: boolean;
}

export type ChartSourceEvent =
  | { kind: "clear"; snapshot: ChartSourceSnapshot }
  | { kind: "replace"; snapshot: ChartSourceSnapshot }
  | {
      kind: "tick";
      snapshot: ChartSourceSnapshot;
      timestamp: number;
      price: number;
      mutationKind: string | null;
    }
  | {
      kind: "prepend";
      snapshot: ChartSourceSnapshot;
      prependedCount: number;
      origin: string | null;
    }
  | { kind: "loading"; snapshot: ChartSourceSnapshot };

type ChartSourceListener = (event: ChartSourceEvent) => void;

const DEFAULT_GRANULARITY_SECONDS = 60;

let snapshot: ChartSourceSnapshot = {
  symbol: null,
  hasData: false,
  granularitySeconds: DEFAULT_GRANULARITY_SECONDS,
  oldestEpoch: null,
  latestEpoch: null,
  hasReachedDepthCeiling: false,
  isLoadingHistory: false,
};

const listeners = new Set<ChartSourceListener>();

function emit(event: ChartSourceEvent) {
  listeners.forEach((listener) => listener(event));
}

function syncChartStore(next: ChartSourceSnapshot) {
  const base = getBaseColumns();
  const lastCandle = base && base.length > 0 ? candleAt(base, base.length - 1) : null;
  const prevCandle = base && base.length >= 2 ? candleAt(base, base.length - 2) : null;

  useChartStore.setState({
    hasData: next.hasData,
    sourceGranularitySeconds: next.granularitySeconds,
    oldestEpoch: next.oldestEpoch,
    latestEpoch: next.latestEpoch,
    hasReachedDepthCeiling: next.hasReachedDepthCeiling,
    isLoadingHistory: next.isLoadingHistory,
    livePrice: lastCandle?.close ?? null,
    priceChange: lastCandle && prevCandle ? lastCandle.close - prevCandle.close : null,
    ohlc: lastCandle
      ? { open: lastCandle.open, high: lastCandle.high, low: lastCandle.low, close: lastCandle.close }
      : null,
  });
}

function updateSnapshot(nextPartial: Partial<ChartSourceSnapshot>): ChartSourceSnapshot {
  snapshot = { ...snapshot, ...nextPartial };
  syncChartStore(snapshot);
  return snapshot;
}

export function getChartSourceSnapshot(): ChartSourceSnapshot {
  return snapshot;
}

export function subscribeChartSource(listener: ChartSourceListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function clearChartSource(symbol: string | null = null) {
  const next = updateSnapshot({
    symbol,
    hasData: false,
    granularitySeconds: DEFAULT_GRANULARITY_SECONDS,
    oldestEpoch: null,
    latestEpoch: null,
    hasReachedDepthCeiling: false,
    isLoadingHistory: false,
  });
  emit({ kind: "clear", snapshot: next });
}

export function replaceChartSource(params: {
  symbol: string;
  granularitySeconds: number;
  firstTs: number;
  lastTs: number;
}) {
  const next = updateSnapshot({
    symbol: params.symbol,
    hasData: hasDataset(),
    granularitySeconds: Math.max(1, Math.floor(Number(params.granularitySeconds) || DEFAULT_GRANULARITY_SECONDS)),
    oldestEpoch: params.firstTs,
    latestEpoch: params.lastTs,
    hasReachedDepthCeiling: false,
    isLoadingHistory: false,
  });
  emit({ kind: "replace", snapshot: next });
  return next;
}

export function setChartSourceLoading(isLoadingHistory: boolean) {
  const next = updateSnapshot({ isLoadingHistory });
  emit({ kind: "loading", snapshot: next });
  return next;
}

export function setChartSourceDepthCeiling(hasReachedDepthCeiling: boolean) {
  return updateSnapshot({ hasReachedDepthCeiling });
}

// ─── Legacy stubs (no live ticks in CSV-only app) ────────────────────────────

export function ingestChartSourceTick(_params: {
  symbol: string;
  timestamp: number;
  price: number;
}): null {
  return null;
}

export function prependChartSourceCandles(_params: {
  symbol: string;
  candles: unknown[];
  origin?: string | null;
  depthCeilingEpoch?: number | null;
}): { prependedCount: number; snapshot: ChartSourceSnapshot } {
  return { prependedCount: 0, snapshot };
}
